<script lang="ts">
  import type { AdminCapability } from "$lib/api/contract";
  import Button from "$lib/components/Button.svelte";

  interface Props {
    capability: AdminCapability;
  }
  const { capability }: Props = $props();
</script>

<!--
  Deliberately rendered inside AdminShell so navigation and account controls stay
  where they were. The copy is uniform for every denied capability and every target:
  it says only that this account lacks the operation, never whether the thing behind
  the route exists.
-->
<section class="denied" aria-labelledby="capability-denied-title">
  <h1 class="page-title" id="capability-denied-title" data-page-title tabindex="-1">
    Page unavailable
  </h1>
  <p class="lead">
    This support account doesn't have access to that operation. Nothing about it is
    shown here.
  </p>
  <dl class="detail">
    <div>
      <dt>Required capability</dt>
      <dd><span class="mono">{capability}</span></dd>
    </div>
  </dl>
  <p class="note">
    If you need it, ask an EpiDev administrator to grant the capability to your account.
  </p>
  <div class="actions">
    <Button size="sm" variant="ghost" href="/admin/entitlements">Back to customers</Button>
  </div>
</section>

<style>
  .denied {
    display: grid;
    gap: var(--space-4);
    align-content: start;
    justify-items: start;
    width: 100%;
    max-width: 62ch;
    padding: var(--space-7) clamp(14px, 2vw, 24px) var(--space-8);
    min-width: 0;
  }
  .page-title {
    margin: 0;
    color: var(--heading);
    font-size: 24px;
    letter-spacing: 0;
  }
  .lead,
  .note {
    margin: 0;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.6;
  }
  .note {
    font-size: 13px;
  }
  .detail {
    margin: 0;
    width: 100%;
  }
  .detail > div {
    display: grid;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid var(--line-soft);
    border-radius: var(--radius-chip);
    background: var(--surface-sunken);
    min-width: 0;
  }
  dt {
    color: var(--muted);
    font-size: 12px;
    letter-spacing: 0;
  }
  dd {
    margin: 0;
    color: var(--heading);
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
  .mono {
    font-family: var(--font-mono);
  }
  .actions {
    margin-top: var(--space-1);
  }
</style>
